import { roundDecimals } from '../math/math';

/**
 * 线性 RGB 转到 sRGB 编码
 * 转换的公式：伽马变换。画布颜色值 V 和光强度值 I 的关系表示为 V = I^(1/2.2)。但直接使用 2.2 作为 Gamma 值在算术运算上会有困难，所以引入了近似方法，将该公式拆分为两个部分：
 * 当 I <= 0.0031308 时，取 V = 12.92I（用线性方法近似）
   当 I > 0.0031308 时，取 V = 1.055 * I^(1/2.4) - 0.055（非线性变换）
  类似地，sRGB 转线性 RGB 把公式反向即可。（以上所有的 I 和 V 都在 0 到 1 的范围内）
  原因：人眼在暗区的色彩辨识度远高于亮区，所以需要放大暗区的变化刻度，将更多色彩编码分给暗区（使用线性变换），将更少的编码分给亮区（使用指数变换）
 * @param color 归一化到 [0, 1] 区间的颜色通道值，如 RGB 中的 R、G、B 其中一个
 * @returns 归一化到 [0, 1] 区间的颜色通道值
 */
export const linearToSrgb = (color: number) => {
  if (color <= 0.0031308) {
    return color * 12.92;
  } else {
    return 1.055 * Math.pow(color, 1 / 2.4) - 0.055;
  }
};

/**
 * sRGB 转到线性 RGB 编码
 * @param color 归一化到 [0, 1] 区间的颜色通道值，如 RGB 中的 R、G、B 其中一个
 * @returns 归一化到 [0, 1] 区间的颜色通道值
 */
export const sRGBToLinear = (color: number) => {
  if (color <= 0.04045) {
    return color / 12.92;
  } else {
    return Math.pow((color + 0.055) / 1.055, 2.4);
  }
};

/**
 * 调整颜色通道值的对比度
 * @param color 归一化到 [0, 1] 区间的颜色通道值，如 RGB 中的 R、G、B 其中一个
 * @param contrast 对比度
 * @returns 归一化到 [0, 1] 区间的颜色通道值
 */
const adjustColorContrast = (color: number, contrast: number): number => {
  return roundDecimals((color - 0.5) * contrast + 0.5, 3);
};

/**
 * 调整 rgb 的对比度
 * @param rgb  归一化到 [0, 1] 区间的 rgb 数组: [r, g, b]
 * @param whiteThrohold 白色阈值，rgb 值归一化到 [0 - 1] 区间，超过阈值认为是白色
 * @param whiteContrast 白色对比度
 * @param baseContrast 其他颜色对比度
 * @returns 归一化到 [0, 1] 区间的 rgb 数组: [r, g, b]
 */
export const applyContrast = (
  rgb: [number, number, number],
  whiteThrohold: number,
  whiteContrast: number,
  baseContrast: number
): [number, number, number] => {
  const [r, g, b] = rgb;
  if (r > whiteThrohold && g > whiteThrohold && b > whiteThrohold) {
    return [
      adjustColorContrast(r, whiteContrast),
      adjustColorContrast(g, whiteContrast),
      adjustColorContrast(b, whiteContrast),
    ];
  } else {
    return [
      adjustColorContrast(r, baseContrast),
      adjustColorContrast(g, baseContrast),
      adjustColorContrast(b, baseContrast),
    ];
  }
};
